/**
 * Collection table view - displays cards in a sortable table
 * Uses shared sort state from parent - clicking headers syncs with SortDropdown
 * Supports compact mode that hides images and reduces padding
 * Supports column visibility settings persisted to localStorage
 *
 * Performance optimizations (Phase 1):
 * - Pre-computed fields used for CMC, price, roles, images, sort keys
 * - Starred state lifted to table level (one atom read vs N)
 * - Single shared CardHoverPreview instead of N wrapper instances
 * - AddToListMenu lazy-mounted on click (eliminates ~22K hooks on mount)
 *
 * Performance optimizations (Phase 2):
 * - CollectionTableRow wrapped in React.memo with custom comparator
 * - Columns definition stabilized via table meta (no star/list/hover closures)
 * - Only rows with actual data or star/list state changes re-render
 *
 * Performance optimizations (Phase 3):
 * - Row virtualization via @tanstack/react-virtual renders only visible rows + overscan
 * - DOM nodes reduced from ~70-96K to ~2-3K (only ~50 visible rows in DOM)
 * - Sticky thead stays visible during scroll
 * - Spacer rows provide correct scroll height without rendering off-screen content
 */
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef, type Row, type VisibilityState } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import type { ReactElement, Ref } from 'react';
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IoCaretDown,
  IoCaretUp,
  IoCheckbox,
  IoCheckmark,
  IoContract,
  IoExpand,
  IoRemoveOutline,
  IoSquareOutline,
  IoStar,
  IoStarOutline,
} from 'react-icons/io5';
import { PiListChecks, PiListChecksFill } from 'react-icons/pi';

import { CardHoverPreviewPortal, calculatePreviewLayout, type PreviewLayout } from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import ColorDots from '@/components/ColorDots/ColorDots';
import KeywordIcons from '@/components/KeywordIcons/KeywordIcons';
import SplitManaCost from '@/components/ManaSymbol/SplitManaCost';
import ProducedMana from '@/components/ProducedMana/ProducedMana';
import RarityBadge from '@/components/RarityBadge/RarityBadge';
import RoleTags from '@/components/RoleTags/RoleTags';
import AddToListMenu from '@/features/lists/components/AddToListMenu';
import { listedOracleIdsAtom } from '@/features/lists/state/listsAtom';
import useUserSettings from '@/features/settings/hooks/api';
import { useToggleStar } from '@/features/starred/hooks/api';
import { starredOracleIdsAtom } from '@/features/starred/state/starredAtom';
import {
  CONDITION_LABELS,
  FINISH_LABELS,
  formatPowerToughnessFull,
  getDisplayManaCost,
  getOracleId,
  getPower,
  getToughness,
  getTypeLine,
} from '@/lib/cardUtils';
import { hasDualFaceImages } from '@/lib/imageUrl';
import { formatPrice } from '@/lib/price';
import type { CardFace, CardLayout, Color, EnrichedCollectionCard, ImageUris } from '@/types/card';

import type { CollectionSort, SortField } from '../hooks/useCollectionSort';
import useCompactMode from '../hooks/useCompactMode';
import useSelectedCard from '../hooks/useSelectedCard';
import {
  collectionTableSettingsAtom,
  resolveCollectionColumnVisibility,
  type CollectionColumnKey,
  type CollectionColumnVisibility,
} from '../state/collectionTableSettings';

// Map table column IDs to sort fields
const COLUMN_TO_SORT_FIELD: Record<string, SortField> = {
  name: 'name',
  type: 'type',
  set: 'set',
  folder: 'folder',
  quantity: 'quantity',
  rarity: 'rarity',
  edhrecRank: 'edhrecRank',
  price: 'price',
  cmc: 'cmc',
  roles: 'roles',
  condition: 'condition',
  language: 'language',
  finish: 'finish',
};

/**
 * Map CollectionColumnKey to TanStack Table column IDs.
 * Most columns use the same key, but some differ due to existing naming.
 */
const COLUMN_KEY_TO_ID: Record<CollectionColumnKey, string> = {
  image: 'image',
  actions: 'actions',
  name: 'name',
  supertype: 'supertype',
  type: 'type',
  subtype: 'subtype',
  fullType: 'fullType',
  colorIdentity: 'colorIdentity',
  cmc: 'cmc',
  manaCost: 'manaCost',
  manaProduced: 'manaProduced',
  set: 'set',
  setNumber: 'setNumber',
  collector_number: 'collector_number',
  rarity: 'rarity',
  released: 'released',
  powerToughness: 'powerToughness',
  power: 'power',
  toughness: 'toughness',
  quantity: 'quantity',
  price: 'price',
  folder: 'folder',
  condition: 'condition',
  language: 'language',
  finish: 'finish',
  roles: 'roles',
  keywords: 'keywords',
  edhrecRank: 'edhrecRank',
  gameChanger: 'gameChanger',
};

/**
 * Convert CollectionColumnVisibility to TanStack VisibilityState.
 * Applies compact mode overrides for the image column.
 */
function toVisibilityState(visibility: CollectionColumnVisibility, isCompact: boolean): VisibilityState {
  const state: VisibilityState = {};
  for (const [key, value] of Object.entries(resolveCollectionColumnVisibility(visibility))) {
    const columnId = COLUMN_KEY_TO_ID[key as CollectionColumnKey];
    // In compact mode, always hide image regardless of visibility setting
    if (key === 'image' && isCompact) {
      state[columnId] = false;
    } else {
      state[columnId] = value;
    }
  }
  return state;
}

export interface CollectionTableProps {
  cards: EnrichedCollectionCard[];
  sort: CollectionSort;
  onSortChange: (field: SortField) => void;
  isSelectionMode?: boolean;
  selectedCardIds?: Set<string>;
  onToggleCard?: (cardId: string) => void;
  onSelectAll?: (ids: string[]) => void;
  onDeselectAll?: () => void;
}

/** Hover preview position and card data for single shared instance */
interface HoverState {
  imageUris: Partial<ImageUris> | null;
  name: string;
  layout: PreviewLayout;
  cardLayout?: CardLayout | null;
  cardFaces?: CardFace[] | null;
}

/**
 * Table meta — passes frequently-changing state to cell renderers
 * without including it in the columns useMemo deps.
 * This prevents column recreation on every star/list toggle.
 */
interface CollectionTableMeta {
  starredOracleIds: Set<string>;
  listedOracleIds: Set<string>;
  toggleStar: (oracleId: string, name: string, scryfallId: string) => void;
  onImageMouseEnter: (
    imageUris: Partial<ImageUris> | null,
    name: string,
    e: React.MouseEvent,
    cardLayout?: CardLayout | null,
    cardFaces?: CardFace[] | null,
  ) => void;
  onImageMouseMove: (e: React.MouseEvent) => void;
  onImageMouseLeave: () => void;
  currency: 'USD' | 'EUR';
}

/** Row heights for virtualizer — measured from actual rendered rows */
const ROW_HEIGHT_NORMAL = 131;
const ROW_HEIGHT_COMPACT = 44.67;

/** Lazy-mount wrapper for AddToListMenu — renders only trigger icon until clicked */
function LazyAddToListTrigger({
  oracleId,
  cardName,
  scryfallId,
  isOnList,
}: {
  oracleId: string;
  cardName: string;
  scryfallId: string;
  isOnList: boolean;
}): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  if (isOpen) {
    // Mount full AddToListMenu (with all its hooks) only when user clicks
    return (
      <AddToListMenu oracleId={oracleId} cardName={cardName} scryfallId={scryfallId} variant="icon" initialOpen dropdownAlign="left" />
    );
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        setIsOpen(true);
      }}
      className={clsx('rounded-lg p-1.5 transition-colors', isOnList ? 'text-teal' : 'text-ash hover:text-teal')}
      aria-label={isOnList ? 'On list - click to manage' : 'Add to list'}
      title={isOnList ? 'On list' : 'Add to list'}
    >
      {isOnList ? <PiListChecksFill className="h-5 w-5" /> : <PiListChecks className="h-5 w-5" />}
    </button>
  );
}

/** Memoized table row — only re-renders when its specific data or star/list status changes */
interface CollectionTableRowProps {
  row: Row<EnrichedCollectionCard>;
  compactMode: boolean;
  isStarred: boolean;
  isOnList: boolean;
  isSelected: boolean;
  onClick: (cardId: string) => void;
  /** Virtual row index — required by @tanstack/react-virtual measureElement */
  virtualIndex: number;
}

const CollectionTableRow = memo(
  forwardRef(function CollectionTableRow(
    { row, compactMode, isSelected, onClick, virtualIndex }: CollectionTableRowProps,
    ref: Ref<HTMLTableRowElement>,
  ): ReactElement {
    return (
      <tr
        ref={ref}
        data-index={virtualIndex}
        onClick={() => onClick(row.original.id)}
        className={clsx('border-storm hover:bg-slate/50 cursor-pointer border-b transition-colors', isSelected && 'bg-aether/10')}
      >
        {row.getVisibleCells().map((cell) => (
          <td
            key={cell.id}
            className={clsx(
              'text-sm',
              (cell.column.columnDef.meta as any)?.pxClass !== undefined
                ? (cell.column.columnDef.meta as any)?.pxClass
                : compactMode
                  ? 'px-3'
                  : 'px-4',
              (cell.column.columnDef.meta as any)?.align === 'right'
                ? 'text-right'
                : (cell.column.columnDef.meta as any)?.align === 'center'
                  ? 'text-center'
                  : 'text-left',
              compactMode ? 'py-1.5 whitespace-nowrap' : 'py-3',
              !(cell.column.columnDef.meta as any)?.allowOverflow && 'overflow-hidden',
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );
  }),
  (prev, next) =>
    prev.row.original === next.row.original &&
    prev.compactMode === next.compactMode &&
    prev.isStarred === next.isStarred &&
    prev.isOnList === next.isOnList &&
    prev.isSelected === next.isSelected &&
    prev.onClick === next.onClick &&
    prev.virtualIndex === next.virtualIndex,
);

/** Minimum width for the flexible Name column (px) */
const MIN_NAME_WIDTH = 250;

export default function CollectionTable({
  cards,
  sort,
  onSortChange,
  isSelectionMode,
  selectedCardIds,
  onToggleCard,
  onSelectAll,
  onDeselectAll,
}: CollectionTableProps): ReactElement {
  const { compactMode, toggleCompactMode } = useCompactMode();
  const { selectCard } = useSelectedCard();
  const { settings } = useUserSettings();
  const tableSettings = useAtomValue(collectionTableSettingsAtom);

  // Lifted starred state — one stable Set read instead of N useIsStarred hooks
  const starredOracleIds = useAtomValue(starredOracleIdsAtom);
  const toggleStar = useToggleStar();

  // Listed oracle IDs — for lazy AddToListMenu "on list" indicator
  const listedOracleIds = useAtomValue(listedOracleIdsAtom);

  // Single shared hover preview state
  const [hoverState, setHoverState] = useState<HoverState | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll container ref for virtualizer
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Track container width for dynamic Name column sizing
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute TanStack visibility state from settings
  const columnVisibility = useMemo(
    () => toVisibilityState(tableSettings.columnVisibility, compactMode),
    [tableSettings.columnVisibility, compactMode],
  );

  const handleRowClick = useCallback(
    (cardId: string) => {
      if (isSelectionMode) {
        onToggleCard?.(cardId);
      } else {
        selectCard(cardId);
      }
    },
    [selectCard, isSelectionMode, onToggleCard],
  );

  // Handle header click - sync with shared sort state
  const handleHeaderClick = (columnId: string) => {
    const sortField = COLUMN_TO_SORT_FIELD[columnId];
    if (sortField) {
      onSortChange(sortField);
    }
  };

  // Shared hover preview handlers
  const handleImageMouseEnter = useCallback(
    (
      imageUris: Partial<ImageUris> | null,
      name: string,
      e: React.MouseEvent,
      cardLayout?: CardLayout | null,
      cardFaces?: CardFace[] | null,
    ) => {
      if (!imageUris) return;
      const isDualFace = hasDualFaceImages(cardLayout, cardFaces);
      const layout = calculatePreviewLayout(e.clientX, e.clientY, isDualFace);
      hoverTimeoutRef.current = setTimeout(() => {
        setHoverState({ imageUris, name, layout, cardLayout, cardFaces });
      }, 250);
    },
    [],
  );

  const handleImageMouseMove = useCallback((e: React.MouseEvent) => {
    setHoverState((prev) => {
      if (!prev) return prev;
      const isDualFace = hasDualFaceImages(prev.cardLayout, prev.cardFaces);
      const layout = calculatePreviewLayout(e.clientX, e.clientY, isDualFace);
      return { ...prev, layout };
    });
  }, []);

  const handleImageMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoverState(null);
  }, []);

  const columns = useMemo<ColumnDef<EnrichedCollectionCard>[]>(() => {
    const selectionColumn: ColumnDef<EnrichedCollectionCard> = {
      id: 'selection',
      header: () => {
        if (!selectedCardIds || !onSelectAll || !onDeselectAll) return null;
        const allIds = cards.map((c) => c.id);
        const allSelected = allIds.length > 0 && allIds.every((id) => selectedCardIds.has(id));
        const someSelected = !allSelected && allIds.some((id) => selectedCardIds.has(id));

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (allSelected || someSelected) {
                onDeselectAll();
              } else {
                onSelectAll(allIds);
              }
            }}
            className="text-ash hover:text-pearl flex items-center justify-center transition-colors"
            aria-label={allSelected ? 'Deselect all' : 'Select all'}
          >
            {allSelected ? (
              <IoCheckbox className="text-aether h-5 w-5" />
            ) : someSelected ? (
              <IoRemoveOutline className="text-aether border-aether h-5 w-5 rounded border" />
            ) : (
              <IoSquareOutline className="h-5 w-5" />
            )}
          </button>
        );
      },
      size: 40,
      enableSorting: false,
      cell: ({ row }) => {
        const isSelected = selectedCardIds?.has(row.original.id) ?? false;
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCard?.(row.original.id);
            }}
            className="text-ash hover:text-pearl inline-flex items-center justify-center transition-colors"
            aria-label={isSelected ? 'Deselect card' : 'Select card'}
          >
            {isSelected ? <IoCheckbox className="text-aether h-5 w-5" /> : <IoSquareOutline className="h-5 w-5" />}
          </button>
        );
      },
      meta: { align: 'center', pxClass: 'px-1' },
    };

    const quantityColumn: ColumnDef<EnrichedCollectionCard> = {
      id: 'quantity',
      accessorKey: 'quantity',
      header: 'Qty',
      size: compactMode ? 38 : 48,
      enableSorting: true,
      cell: ({ getValue }) => {
        const qty = getValue<number>();
        return qty > 1 ? <span className="block text-right font-mono text-sm text-zinc-400">{qty}×</span> : null;
      },
      meta: { align: 'right', pxClass: compactMode ? null : 'px-1' },
    };

    return [
      // Selection checkbox column (only when selection mode is active)
      ...(isSelectionMode ? [selectionColumn] : []),
      // Non-compact: Quantity before image
      ...(compactMode ? [] : [quantityColumn]),
      // Image column — uses pre-computed display_image_uris + shared hover preview via table meta
      {
        id: 'image',
        header: '',
        size: 80,
        enableSorting: false,
        cell: ({ row, table }) => {
          const meta = table.options.meta as CollectionTableMeta;
          const imageUris = row.original.display_image_uris;
          const card = row.original.card;
          return (
            <div
              className="inline-block"
              onMouseEnter={(e) => meta.onImageMouseEnter(imageUris, card.name, e, card.layout, card.card_faces)}
              onMouseMove={meta.onImageMouseMove}
              onMouseLeave={meta.onImageMouseLeave}
            >
              <CardImage imageUris={imageUris} name={card.name} size="small" className="h-11 w-8 rounded" />
            </div>
          );
        },
        meta: { pxClass: 'px-1' },
      },
      // Star & List actions column — reads starred/listed state from table meta
      {
        id: 'actions',
        header: '',
        size: compactMode ? 60 : 40,
        enableSorting: false,
        meta: { allowOverflow: true },
        cell: ({ row, table }) => {
          const meta = table.options.meta as CollectionTableMeta;
          const oracleId = getOracleId(row.original.card);
          const isStarred = oracleId ? meta.starredOracleIds.has(oracleId) : false;
          const isOnList = oracleId ? meta.listedOracleIds.has(oracleId) : false;
          return (
            <div className={clsx('flex items-center gap-1', compactMode ? 'flex-row' : 'flex-col')}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  meta.toggleStar(oracleId ?? '', row.original.card.name, row.original.scryfall_id);
                }}
                className="text-ash hover:text-gold transition-colors"
                aria-label={isStarred ? 'Remove from starred' : 'Add to starred'}
              >
                {isStarred ? <IoStar className="text-gold h-4 w-4" /> : <IoStarOutline className="h-4 w-4" />}
              </button>
              <div onClick={(e) => e.stopPropagation()}>
                <LazyAddToListTrigger
                  oracleId={oracleId ?? ''}
                  cardName={row.original.card.name}
                  scryfallId={row.original.scryfall_id}
                  isOnList={isOnList}
                />
              </div>
            </div>
          );
        },
      },
      // Compact: Quantity after actions
      ...(compactMode ? [quantityColumn] : []),
      // Name — flexible column: absorbs all remaining width
      {
        accessorFn: (row) => row.card.name,
        id: 'name',
        header: 'Name',
        meta: { flexible: true },
        cell: ({ row }) => {
          const name = row.original.card.name;
          const parts = name.split(' // ');

          if (!compactMode && parts.length > 1) {
            return (
              <span className="text-pearl font-medium">
                {parts.map((part, i) => (
                  <span key={i} className="block">
                    {part}
                  </span>
                ))}
              </span>
            );
          }

          return <span className="text-pearl font-medium">{name}</span>;
        },
      },
      // Roles — uses pre-computed classification from enrichment
      {
        id: 'roles',
        header: 'Roles',
        size: 140,
        enableSorting: true,
        cell: ({ row }) => {
          return <RoleTags roles={row.original.classification.active_roles} iconOnly maxVisible={4} className="flex-nowrap!" />;
        },
      },
      // Keywords — dedicated column with mana-font ability icons
      {
        id: 'keywords',
        header: 'Keywords',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => {
          return <KeywordIcons keywords={row.original.card.keywords} maxVisible={5} />;
        },
      },
      {
        accessorFn: (row) => row.card.edhrec_rank ?? null,
        id: 'edhrecRank',
        header: 'EDHREC Rank',
        size: 150,
        meta: { align: 'right' },
        enableSorting: true,
        cell: ({ getValue }) => <span className="text-silver block text-right font-mono">{getValue<number | null>() ?? '—'}</span>,
      },
      {
        id: 'gameChanger',
        header: 'Game Changer',
        size: 145,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.card.game_changer === true ? (
            <IoCheckmark className="text-gold h-5 w-5" role="img" aria-label="Game Changer" />
          ) : null,
      },
      // Supertype — from classification
      {
        accessorFn: (row) => row.classification.super_type,
        id: 'supertype',
        header: 'Supertype',
        size: 110,
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue<string>();
          if (!value) return null;
          return <span className="text-silver capitalize">{value}</span>;
        },
      },
      // Type — shows classification.main_type (was card.type_line)
      {
        accessorFn: (row) => row.classification.main_type,
        id: 'type',
        header: 'Type',
        size: 180,
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <span className="text-silver capitalize" title={value}>
              {value}
            </span>
          );
        },
      },
      // Subtype — from classification
      {
        accessorFn: (row) => row.classification.sub_type,
        id: 'subtype',
        header: 'Subtype',
        size: 180,
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue<string>();
          if (!value) return null;
          return (
            <span className="text-silver truncate capitalize" title={value}>
              {value}
            </span>
          );
        },
      },
      // Full Type — the complete type line from card
      {
        accessorFn: (row) => getTypeLine(row.card),
        id: 'fullType',
        header: 'Full Type',
        size: 350,
        enableSorting: false,
        cell: ({ row }) => {
          const typeLine = getTypeLine(row.original.card) ?? '';
          const parts = typeLine.split(' // ');
          if (!compactMode && parts.length > 1) {
            return (
              <span className="text-silver" title={typeLine}>
                {parts.map((part, i) => (
                  <span key={i} className="block">
                    {part}
                  </span>
                ))}
              </span>
            );
          }
          return (
            <span className="text-silver truncate" title={typeLine ?? undefined}>
              {typeLine}
            </span>
          );
        },
      },
      // Color Identity
      {
        accessorFn: (row) => row.card.color_identity,
        id: 'colorIdentity',
        header: () => <span title="Color Identity">CID</span>,
        size: 120,
        enableSorting: false,
        cell: ({ row }) => <ColorDots colors={row.original.card.color_identity} />,
      },
      // CMC — uses pre-computed display_cmc
      {
        accessorFn: (row) => row.display_cmc,
        id: 'cmc',
        header: 'CMC',
        size: 75,
        enableSorting: true,
        cell: ({ getValue }) => {
          const cmc = getValue<number>();
          return <span className="text-silver font-mono">{cmc}</span>;
        },
        meta: { align: 'center' },
      },
      // Mana Cost — for multi-faced cards, show both costs with separator
      {
        accessorFn: (row) => getDisplayManaCost(row.card),
        id: 'manaCost',
        header: 'Mana cost',
        size: 150,
        enableSorting: false,
        cell: ({ row }) => {
          const manaCost = getDisplayManaCost(row.original.card);
          if (!manaCost) return <span className="text-ash">—</span>;
          return <SplitManaCost cost={manaCost} iconSize={20} />;
        },
      },
      // Mana Produced — shows mana symbol icons for produced colors
      {
        accessorFn: (row) => row.card.produced_mana,
        id: 'manaProduced',
        header: 'Produces',
        size: 120,
        enableSorting: false,
        cell: ({ getValue }) => {
          const colors = getValue<Color[] | null>();
          return <ProducedMana colors={colors ?? null} iconSize={20} />;
        },
      },
      // Rarity
      {
        accessorFn: (row) => row.card.rarity,
        id: 'rarity',
        header: 'Rarity',
        size: 90,
        cell: ({ row }) => <RarityBadge rarity={row.original.card.rarity} variant="icon-text" className="text-[20px]!" />,
      },
      // P/T (combined power/toughness)
      {
        accessorFn: (row) => formatPowerToughnessFull(row.card),
        id: 'powerToughness',
        header: 'P/T',
        size: 70,
        enableSorting: false,
        cell: ({ getValue }) => {
          const pt = getValue<string | null>();
          if (!pt) return null;
          return <span className="text-silver font-mono">{pt}</span>;
        },
        meta: { align: 'center' },
      },
      // Power
      {
        accessorFn: (row) => getPower(row.card),
        id: 'power',
        header: 'Power',
        size: 65,
        enableSorting: false,
        cell: ({ getValue }) => {
          const power = getValue<string | null>();
          if (!power) return null;
          return <span className="text-silver font-mono">{power}</span>;
        },
        meta: { align: 'center' },
      },
      // Toughness
      {
        accessorFn: (row) => getToughness(row.card),
        id: 'toughness',
        header: 'Tough.',
        size: 65,
        enableSorting: false,
        cell: ({ getValue }) => {
          const toughness = getValue<string | null>();
          if (!toughness) return null;
          return <span className="text-silver font-mono">{toughness}</span>;
        },
        meta: { align: 'center' },
      },
      // Set # (combined set code + collector number)
      {
        accessorFn: (row) => `${row.card.set.toUpperCase()} #${row.card.collector_number}`,
        id: 'setNumber',
        header: 'Set #',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => {
          const setCode = row.original.card.set.toUpperCase();
          const collectorNum = row.original.card.collector_number.padStart(3, '0');
          return (
            <span className="text-silver font-mono">
              {setCode} #{collectorNum}
            </span>
          );
        },
      },
      // Collector Number
      {
        accessorFn: (row) => row.card.collector_number,
        id: 'collector_number',
        header: '#',
        size: 55,
        enableSorting: false,
        cell: ({ row }) => <span className="text-silver font-mono">{row.original.card.collector_number}</span>,
      },
      // Set
      {
        accessorFn: (row) => row.card.set_name,
        id: 'set',
        header: 'Set',
        size: 380,
        cell: ({ row }) => (
          <span className="text-silver">
            {row.original.card.set_name}
            <span className="text-ash ml-1">({row.original.card.set.toUpperCase()})</span>
          </span>
        ),
      },
      // Released
      {
        accessorFn: (row) => row.card.released_at,
        id: 'released',
        header: 'Released',
        size: 110,
        enableSorting: false,
        cell: ({ getValue }) => {
          const date = getValue<string>();
          if (!date) return null;
          return <span className="text-silver font-mono text-xs">{date}</span>;
        },
      },
      // Folder
      {
        accessorKey: 'folder_path',
        id: 'folder',
        header: 'Folder',
        size: 200,
        cell: ({ row }) => {
          const path = row.original.folder_path;
          if (!path) return <span className="text-ash">—</span>;
          return (
            <span className="text-silver max-w-30 truncate" title={path}>
              {path}
            </span>
          );
        },
      },
      // Condition
      {
        accessorKey: 'condition',
        id: 'condition',
        header: 'Condition',
        size: 120,
        cell: ({ getValue }) => <span className="text-silver">{CONDITION_LABELS[getValue<keyof typeof CONDITION_LABELS>()]}</span>,
      },
      // Language
      {
        accessorKey: 'language',
        id: 'language',
        header: 'Language',
        size: 120,
        cell: ({ row }) => <span className="text-silver">{row.original.language_label}</span>,
      },
      // Finish
      {
        accessorKey: 'finish',
        id: 'finish',
        header: 'Finish',
        size: 90,
        cell: ({ getValue }) => <span className="text-silver">{FINISH_LABELS[getValue<keyof typeof FINISH_LABELS>()]}</span>,
      },
      // Price — uses pre-computed price_usd / price_eur, reads currency from table meta
      {
        accessorFn: (row) => row.price_usd ?? row.price_eur ?? 0,
        id: 'price',
        header: 'Price',
        size: 110,
        cell: ({ row, table }) => {
          const meta = table.options.meta as CollectionTableMeta;
          const price = (meta.currency === 'EUR' ? row.original.price_eur : row.original.price_usd) ?? 0;
          return price > 0 ? (
            <span className="text-silver font-mono">{formatPrice(price, meta.currency)}</span>
          ) : (
            <span className="text-ash">—</span>
          );
        },
      },
    ];
  }, [compactMode, isSelectionMode, selectedCardIds, cards, onToggleCard, onSelectAll, onDeselectAll]);

  const tableMeta: CollectionTableMeta = useMemo(
    () => ({
      starredOracleIds,
      listedOracleIds,
      toggleStar,
      onImageMouseEnter: handleImageMouseEnter,
      onImageMouseMove: handleImageMouseMove,
      onImageMouseLeave: handleImageMouseLeave,
      currency: settings.currency,
    }),
    [starredOracleIds, listedOracleIds, toggleStar, handleImageMouseEnter, handleImageMouseMove, handleImageMouseLeave, settings.currency],
  );

  const table = useReactTable({
    data: cards,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility,
    },
    meta: tableMeta,
  });

  // Compute colgroup widths: fixed columns use their size, Name absorbs remainder
  const colWidths = useMemo(() => {
    const visibleColumns = table.getVisibleLeafColumns();
    let fixedTotal = 0;
    for (const col of visibleColumns) {
      if (!(col.columnDef.meta as any)?.flexible) {
        fixedTotal += col.getSize();
      }
    }
    const remaining = containerWidth - fixedTotal;
    const nameWidth = Math.max(remaining, MIN_NAME_WIDTH);

    return visibleColumns.map((col) => ((col.columnDef.meta as any)?.flexible ? nameWidth : col.getSize()));
  }, [table, containerWidth, columnVisibility, isSelectionMode]);

  // Row virtualization — only render visible rows + overscan buffer
  const rows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => (compactMode ? ROW_HEIGHT_COMPACT : ROW_HEIGHT_NORMAL),
    overscan: 15,
  });

  // Reset virtualizer cached sizes when compact mode changes row height estimate
  useEffect(() => {
    rowVirtualizer.measure();
  }, [compactMode, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;

  return (
    <div ref={scrollContainerRef} className="border-storm min-h-0 flex-1 overflow-auto rounded-lg border">
      <table className="w-full" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          {colWidths.map((w, i) => (
            <col key={i} style={{ width: w }} />
          ))}
        </colgroup>
        <thead className="bg-slate sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                const sortField = COLUMN_TO_SORT_FIELD[header.id];
                const isSortable = !!sortField;
                const sortDirection = sortField && sort.field === sortField ? sort.direction : false;
                const isLastColumn = index === headerGroup.headers.length - 1;

                return (
                  <th
                    key={header.id}
                    className={clsx(
                      'text-ash text-xs font-medium tracking-wider uppercase',
                      (header.column.columnDef.meta as any)?.align === 'right'
                        ? 'text-right'
                        : (header.column.columnDef.meta as any)?.align === 'center'
                          ? 'text-center'
                          : 'text-left',
                      (header.column.columnDef.meta as any)?.pxClass !== undefined
                        ? (header.column.columnDef.meta as any)?.pxClass
                        : compactMode
                          ? 'px-3'
                          : 'px-4',
                      compactMode ? 'py-2' : 'py-3',
                      isSortable && 'hover:text-pearl cursor-pointer select-none',
                    )}
                    aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : undefined}
                  >
                    <div
                      className={clsx(
                        'flex items-center gap-1',
                        (header.column.columnDef.meta as any)?.align === 'right'
                          ? 'justify-end'
                          : (header.column.columnDef.meta as any)?.align === 'center'
                            ? 'justify-center'
                            : 'justify-start',
                      )}
                    >
                      {isSortable ? (
                        <button
                          type="button"
                          onClick={() => handleHeaderClick(header.id)}
                          className={clsx(
                            'focus-visible:ring-aether flex min-w-0 flex-1 items-center gap-1 rounded text-inherit uppercase focus-visible:ring-2 focus-visible:outline-none',
                            (header.column.columnDef.meta as any)?.align === 'right' ? 'justify-end' : 'text-left',
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDirection === 'asc' && <IoCaretUp className="h-3 w-3 shrink-0" aria-hidden="true" />}
                          {sortDirection === 'desc' && <IoCaretDown className="h-3 w-3 shrink-0" aria-hidden="true" />}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                      {isLastColumn && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCompactMode();
                          }}
                          className="text-ash hover:text-pearl ml-auto rounded p-1 transition-colors"
                          aria-label={compactMode ? 'Expand table' : 'Compact table'}
                          title={compactMode ? 'Expand table' : 'Compact table'}
                        >
                          {compactMode ? <IoExpand className="h-4 w-4" /> : <IoContract className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 && (
            <tr aria-hidden="true">
              <td colSpan={table.getVisibleLeafColumns().length} style={{ height: paddingTop, padding: 0, border: 0 }} />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <CollectionTableRow
                ref={rowVirtualizer.measureElement}
                key={row.id}
                row={row}
                compactMode={compactMode}
                isStarred={starredOracleIds.has(getOracleId(row.original.card) ?? '')}
                isOnList={listedOracleIds.has(getOracleId(row.original.card) ?? '')}
                isSelected={selectedCardIds?.has(row.original.id) ?? false}
                onClick={handleRowClick}
                virtualIndex={virtualRow.index}
              />
            );
          })}
          {paddingBottom > 0 && (
            <tr aria-hidden="true">
              <td colSpan={table.getVisibleLeafColumns().length} style={{ height: paddingBottom, padding: 0, border: 0 }} />
            </tr>
          )}
        </tbody>
      </table>

      {/* Single shared hover preview — replaces 2,755 CardHoverPreview wrapper instances */}
      {hoverState && (
        <CardHoverPreviewPortal
          imageUris={hoverState.imageUris}
          name={hoverState.name}
          layout={hoverState.layout}
          cardLayout={hoverState.cardLayout}
          cardFaces={hoverState.cardFaces}
        />
      )}
    </div>
  );
}
